import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import * as Icons from "lucide-react";
import { fmtDateTime } from "./docConstants";

const KIND = {
  client: { label: "Client / family", color: "#1C4358", icon: "User" },
  external: { label: "Partner / vendor", color: "#2C6480", icon: "Building2" },
  professional: { label: "Professional", color: "#7C3AED", icon: "Scale" },
};
const STATUS = {
  sent: { label: "Sent", color: "#2F7D5A", bg: "#E9F5EF" },
  delivered: { label: "Sent", color: "#2F7D5A", bg: "#E9F5EF" },
  queued: { label: "Queued", color: "#B7791F", bg: "#FDF6E7" },
  failed: { label: "Failed", color: "#9C1E22", bg: "#FDECEC" },
  in_app_only: { label: "In-app only", color: "#6B7280", bg: "#F1F5F8" },
};

// PROMPT 13G — staff-only "Reminders Sent" trail (reads db.doc_reminders via /doc-center endpoint).
export default function RemindersTrail({ docId, reloadToken }) {
  const [rows, setRows] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/doc-center/documents/${docId}/reminders`); setRows(data.reminders); }
    catch { setRows([]); }
  }, [docId]);
  useEffect(() => { load(); }, [load, reloadToken]);

  if (rows === null) return null;

  return (
    <div className="rounded-xl border border-[#E6EDF2] bg-white p-4" data-testid="detail-reminders-trail">
      <h2 className="text-[13px] font-bold text-[#1C4358] mb-2 flex items-center gap-1.5">
        <Icons.BellRing size={14} /> Reminders sent
        {rows.length > 0 && <span className="text-[11px] font-semibold text-[#94A3B8]">({rows.length})</span>}
      </h2>
      {!rows.length ? (
        <div className="text-[12px] text-[#94A3B8]" data-testid="reminders-trail-empty">No reminders sent yet.</div>
      ) : (
        <ul className="space-y-2.5 max-h-[280px] overflow-y-auto">
          {rows.map((r) => {
            const k = KIND[r.recipient_kind] || KIND.client;
            const s = STATUS[r.delivery_status] || STATUS.queued;
            return (
              <li key={r.id} className="flex items-start gap-2 text-[12px]" data-testid={`reminder-row-${r.id}`}>
                <span className="h-6 w-6 rounded-md flex items-center justify-center shrink-0 mt-0.5" style={{ backgroundColor: `${k.color}14`, color: k.color }}>
                  {React.createElement(Icons[k.icon] || Icons.User, { size: 13 })}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-[#252221]">{r.recipient_name}</span>
                    <span className="text-[10.5px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: `${k.color}12`, color: k.color }}>{k.label}</span>
                    <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded" style={{ backgroundColor: s.bg, color: s.color }} data-testid={`reminder-status-${r.id}`}>{s.label}</span>
                    {r.override && <span className="text-[10.5px] font-semibold px-1.5 py-0.5 rounded bg-[#FDECEC] text-[#9C1E22] flex items-center gap-0.5" data-testid={`reminder-override-${r.id}`}><Icons.RefreshCw size={10} /> Resent</span>}
                  </div>
                  <div className="text-[#6B7280] mt-0.5">
                    Please {r.action_label} · {r.recipient_email}
                  </div>
                  {r.note && <div className="text-[#4B5563] italic mt-0.5">“{r.note}”</div>}
                  <div className="text-[#94A3B8] mt-0.5">{fmtDateTime(r.sent_at)} · by {r.sent_by}</div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
